import React, { useState, useEffect, useMemo, useRef } from "react";
import { Grid, Card, Button, Skeleton } from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getRejectedProducts } from "../../../../api/overView";

/** Utils */
const cleanVendorName = (vendor) => {
  if (!vendor) return "N/A";
  const parts = String(vendor).trim().split(/\s{2,}/); // split by two or more spaces
  return parts.length > 1 ? parts[1] : vendor;
};

const toYyyyMmDd = (dLike) => {
  const d = dLike ? new Date(dLike) : new Date();
  d.setHours(0, 0, 0, 0);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/** Small row component */
const RejectedItem = ({ code, vendor, loading }) => (
  <div className="bg-white rounded p-2 mb-2 text-sm shadow-sm">
    {loading ? (
      <>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="40%" height={20} />
      </>
    ) : (
      <div className="flex items-start gap-2">
        <FiberManualRecordIcon
          sx={{
            color: "#ef4444",
            fontSize: "12px",
            marginTop: "4px",
          }}
        />
        <div>
          <div className="font-bold">SAP: {code ?? "—"}</div>
          <div className="text-xs text-gray-500">
            Vendor: {cleanVendorName(vendor)}
          </div>
        </div>
      </div>
    )}
  </div>
);

const RevenueImpactCard = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const selectedDate = useSelector((state) => state.customization.selectedDate);

  const [componentState, setComponentState] = useState({
    rejectedCount: 0,
    rejectedProducts: [],
    loading: true,
  });

  // Normalize the date to a stable YYYY-MM-DD key
  const dateKey = useMemo(() => toYyyyMmDd(selectedDate), [selectedDate]);

  // If your API expects a Date object, create one at local midnight for dateKey
  // const dateParam = useMemo(() => {
  //   // 00:00 local time for the chosen day
  //   const d = new Date(`${dateKey}T00:00:00`);
  //   if (isNaN(d.getTime())) {
  //     const fallback = new Date();
  //     fallback.setHours(0, 0, 0, 0);
  //     return fallback;
  //   }
  //   return d;
  // }, [dateKey]);

  // Guard to ignore stale requests
  const latestReqIdRef = useRef(0);

  useEffect(() => {
  const abortController = new AbortController();
  const reqId = ++latestReqIdRef.current;

  setComponentState({
    rejectedCount: 0,
    rejectedProducts: [],
    loading: true,
  });

  (async () => {
    try {
      const responseData = await getRejectedProducts(
        new Date(`${dateKey}T00:00:00`),
        storeId,
        abortController.signal
      );

      if (reqId !== latestReqIdRef.current) return;

      const items =
        (Array.isArray(responseData?.result) && responseData.result) ||
        (Array.isArray(responseData?.data?.result) && responseData.data.result) ||
        [];

      const totalCount =
        responseData?.total_count ??
        responseData?.data?.total_count ??
        items.length;

      setComponentState({
        rejectedCount: totalCount,
        rejectedProducts: totalCount > 0 ? items : [],
        loading: false,
      });
    } catch (error) {
      if (reqId !== latestReqIdRef.current) return;
      if (error?.name === "AbortError") return;

      setComponentState({
        rejectedCount: 0,
        rejectedProducts: [],
        loading: false,
      });
    }
  })();

  return () => abortController.abort();
}, [dateKey, storeId]);



  return (
    <Grid item xs={12} sm={12} md={3.0} lg={3.0} xl={3.0}>
      <Card
        className="border relative border-gray-300"
        sx={{
          height: "276px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
        }}>
        <div className="flex w-full flex-col gap-1 p-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <WarningAmber className="bg-red-500 text-white rounded-full p-2 text-6xl" />
              <div style={{ width: componentState.loading ? "120px" : "auto" }}>
                {componentState.loading ? (
                  <>
                    <Skeleton variant="text" width="100%" height={40} />
                    <Skeleton variant="text" width="80%" height={28} />
                  </>
                ) : (
                  <>
                    <p className="text-3xl">{componentState.rejectedCount}</p>
                    <p className="text-lg font-semibold">Rejected Items</p>
                  </>
                )}
              </div>
            </div>

            {!componentState.loading && componentState.rejectedProducts.length > 0 && (
              <Button
                className="absolute text-xs right-2 top-2"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "small" }} />}
                onClick={() =>
                  navigate(
                    `/main/stores/storeinsight/tableview/${storeId}/REJECTED`
                  )
                }
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#ef4444",
                    textDecoration: "underline",
                  },
                }}>
                View all
              </Button>
            )}
          </div>

          <div className="bg-slate-100 flex-grow overflow-y-auto h-[184px] p-2 rounded-lg scrollbar">
            <div className="text-xs text-gray-500 mb-2 italic">
              Rejected products
            </div>

            {componentState.loading ? (
              [...Array(3)].map((_, index) => (
                <RejectedItem key={index} loading={true} />
              ))
            )  : componentState.rejectedCount > 0 ? (
              componentState.rejectedProducts.map((item, index) => (
                <RejectedItem
                  key={item._id ?? index}
                  code={item.Code ?? item.code ?? item.sap_code}
                  vendor={item.Vendor ?? item.vendor}
                  loading={false}
                />
              ))
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                No rejected items
              </div>
            )}
          </div>
        </div>
      </Card>
    </Grid>
  );
};

export default RevenueImpactCard;
